import React, { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

export type ToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
    checked?: boolean;
    disabled?: boolean;
    label?: ReactNode;
    className?: string;
    onChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
};

/**
 * Design-system toggle (switch).
 * Controlled: pass `checked` + `onChange(nextChecked)`.
 */
const Toggle = ({
    checked = false,
    disabled = false,
    label,
    id,
    name,
    className = '',
    onChange,
    ...rest
}: ToggleProps) => {
    const inputId = id || name;

    return (
        <label
            className={cn(
                'toggle relative inline-flex cursor-pointer items-center gap-12 text-ink select-none',
                checked && 'toggle_checked',
                disabled && 'toggle_disabled cursor-default pointer-events-none opacity-50',
                className,
            )}
            htmlFor={inputId}
        >
            <input
                id={inputId}
                name={name}
                type="checkbox"
                className="toggle__input peer pointer-events-none absolute h-0 w-0 opacity-0"
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange?.(event.target.checked, event)}
                {...rest}
            />
            <span
                className={cn(
                    'toggle__track relative h-24 w-[44px] shrink-0 rounded-full bg-secondary-light-grey transition-colors',
                    checked && 'bg-green-accent',
                    'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-pink-accent',
                )}
                aria-hidden="true"
            >
                <span
                    className={cn(
                        'toggle__thumb absolute top-2 left-2 h-20 w-20 rounded-full bg-white shadow-toggle transition-transform',
                        checked && 'translate-x-20',
                    )}
                />
            </span>
            {label ? <span className="toggle__label text-m text-ink">{label}</span> : null}
        </label>
    );
};

export default Toggle;
